/**
 * Portal Service catalog + service-request contracts for the temporary Services mock
 * (see environment.servicesApiUrl). To be aligned with the real backend once available.
 *
 * The Portal only ever captures the first workflow step of a service (a dynamic form);
 * everything after submission is owned internally by pharmacy staff in Knostos.
 */

export enum ServiceRequestStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export interface ServiceCategory {
  id: string;
  name: string;
  services: Service[];
}

export interface Service {
  id: string;
  categoryId: string;
  title: string;
  description?: string;
  /** Backend form definition id from getServicesByCatalog (`formId`). */
  formId: string;
  /** formio JSON schema string for the first (and, in Phase 1, only) workflow step. */
  formSchema: string;
  allowsPrivateRequests: boolean;
}

export interface ServiceFormPayload {
  schema: string;
  inputData: string;
}

export interface ServiceRequestSummary {
  id: string;
  serviceId: string;
  serviceTitle: string;
  status: ServiceRequestStatus;
  isPrivate: boolean;
  submittedAt: string;
  updatedAt: string;
  lastUpdatedAt: string;
  /** Account-scoped lists can include other account members' requests; used to distinguish "yours" from theirs. */
  submittedByName: string;
}

export interface ServiceRequestDetail extends ServiceRequestSummary {
  notifyWhenReady: boolean;
  form: ServiceFormPayload;
}

export interface ServiceDraft {
  id: string;
  serviceId: string;
  serviceTitle: string;
  formId: string;
  lastEditedAt: string;
  updatedAt: string;
  isPrivate: boolean;
  notifyWhenReady: boolean;
  form: ServiceFormPayload;
}

export interface ServiceDraftSaveRequest {
  serviceId: string;
  isPrivate: boolean;
  notifyWhenReady: boolean;
  form: ServiceFormPayload;
}

export interface ServiceRequestSubmitRequest extends ServiceDraftSaveRequest {
  draftId?: string;
}

export interface ServiceApiError {
  code: string;
  message: string;
}

/** 3-segment progress bar fill count shown on My Requests rows. */
export function serviceRequestProgressSegments(
  status: ServiceRequestStatus
): number {
  switch (status) {
    case ServiceRequestStatus.PENDING:
    case ServiceRequestStatus.REJECTED:
      return 1;
    case ServiceRequestStatus.IN_PROGRESS:
      return 2;
    case ServiceRequestStatus.COMPLETED:
      return 3;
    default:
      return 1;
  }
}

export function serviceRequestStatusBadgeClass(
  status: ServiceRequestStatus
): string {
  switch (status) {
    case ServiceRequestStatus.PENDING:
      return 'is-pending';
    case ServiceRequestStatus.IN_PROGRESS:
      return 'is-in-progress';
    case ServiceRequestStatus.COMPLETED:
      return 'is-completed';
    case ServiceRequestStatus.REJECTED:
      return 'is-rejected';
    default:
      return 'is-pending';
  }
}

export function serviceFormFieldLabels(
  schema: string | undefined
): Record<string, string> {
  if (!schema) {
    return {};
  }
  try {
    const parsed = JSON.parse(schema);
    const labels: Record<string, string> = {};
    const visit = (components: unknown): void => {
      if (!Array.isArray(components)) {
        return;
      }
      for (const component of components) {
        if (!component || typeof component !== 'object') {
          continue;
        }
        const item = component as {
          key?: string;
          label?: string;
          type?: string;
          components?: unknown;
          columns?: { components?: unknown }[];
        };
        if (item.key && item.label && item.type !== 'button') {
          labels[item.key] = item.label;
        }
        visit(item.components);
        if (Array.isArray(item.columns)) {
          item.columns.forEach((column) => visit(column.components));
        }
      }
    };
    visit(parsed?.components);
    return labels;
  } catch {
    return {};
  }
}

export function parseServiceFormInputData(
  inputData: string | undefined
): Record<string, unknown> {
  if (!inputData) {
    return {};
  }
  try {
    let parsed: unknown = JSON.parse(inputData);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return extractServiceFormValues(parsed);
  } catch {
    return {};
  }
}

export function extractServiceFormValues(
  event: unknown
): Record<string, unknown> {
  if (!event || typeof event !== 'object' || Array.isArray(event)) {
    return {};
  }

  const record = event as Record<string, unknown>;
  const nested = unwrapFormioDataLayer(record['data']);
  const values = { ...(nested ?? record) };

  // delete values.metadata;
  // delete values.changed;
  // delete values.flags;
  // delete values.isModified;
  // delete values.isValid;
  // delete values.state;
  // if (values.submit === true || values.submit === false) {
  //   delete values.submit;
  // }

  return values;
}

function unwrapFormioDataLayer(value: unknown): Record<string, unknown> | null {
  let current = value;
  if (typeof current === 'string') {
    try {
      current = JSON.parse(current);
    } catch {
      return null;
    }
  }
  if (!current || typeof current !== 'object' || Array.isArray(current)) {
    return null;
  }
  return current as Record<string, unknown>;
}

export function serializeServiceFormInputData(event: unknown): string {
  return JSON.stringify(extractServiceFormValues(event));
}

export function asServiceId(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  return String(value);
}

export function serviceIdsEqual(a: unknown, b: unknown): boolean {
  const left = asServiceId(a);
  const right = asServiceId(b);
  return left !== '' && left === right;
}

export function mapServiceFormSchema(raw: unknown): string {
  return asFormField(asRecord(raw)['schema']);
}

function asFormField(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

function asRecord(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

export function mapServiceCatalog(raw: unknown): ServiceCategory {
  const item = asRecord(raw);
  return {
    id: asServiceId(item['id']),
    name: String(item['name'] ?? ''),
    services: [],
  };
}

export function mapCatalogService(raw: unknown, catalogId: string): Service {
  const item = asRecord(raw);
  const description = String(item['description'] ?? '').trim();
  return {
    id: asServiceId(item['id']),
    categoryId: catalogId,
    title: String(item['name'] ?? item['title'] ?? ''),
    description,
    formId: asServiceId(item['formId']),
    formSchema: '',
    allowsPrivateRequests: false,
  };
}

export function mapServiceRequestSubmitResponse(raw: unknown): {
  id: string;
  message?: string;
} {
  const item = asRecord(raw);
  const message = item['message'];
  return {
    id: asServiceId(item['id']),
    ...(message != null && message !== ''
      ? { message: String(message) }
      : {}),
  };
}

export function mapServiceRequestSummary(raw: unknown): ServiceRequestSummary {
  const item = asRecord(raw);
  return {
    id: asServiceId(item['id']),
    serviceId: asServiceId(item['serviceId']),
    serviceTitle: String(item['serviceTitle'] ?? item['title'] ?? ''),
    status:
      (item['status'] as ServiceRequestStatus) ?? ServiceRequestStatus.PENDING,
    isPrivate: Boolean(item['isPrivate']),
    submittedAt: String(item['submittedAt'] ?? item['createdAt'] ?? ''),
    lastUpdatedAt: String(
      item['lastUpdatedAt'] ?? item['updatedAt'] ?? item['createdAt'] ?? ''
    ),
    updatedAt: String(item['updatedAt'] ?? ''),
    submittedByName: String(item['submittedByName'] ?? ''),
  };
}

function readFormPayload(raw: unknown): ServiceFormPayload {
  const item = asRecord(raw);
  const form = asRecord(item['form']);
  return {
    schema: asFormField(form['schema'] ?? item['schema']),
    inputData: asFormField(
      form['inputData'] ??
        form['input_data'] ??
        item['inputData'] ??
        item['input_data']
    ),
  };
}

export function mapServiceRequestDetail(raw: unknown): ServiceRequestDetail {
  const item = asRecord(raw);
  return {
    ...mapServiceRequestSummary(raw),
    notifyWhenReady: Boolean(
      item['isNotificationEnabled'] ?? item['notifyWhenReady']
    ),
    form: readFormPayload(raw),
  };
}

export function mapServiceDraftListItem(raw: unknown): ServiceDraft {
  const item = asRecord(raw);
  const form = asRecord(item['form']);
  return {
    id: asServiceId(item['id']),
    serviceId: asServiceId(item['serviceId']),
    serviceTitle: String(item['title'] ?? item['serviceTitle'] ?? ''),
    formId: asServiceId(item['formId'] ?? form['id'] ?? form['formId']),
    lastEditedAt: String(item['createdAt'] ?? item['lastEditedAt'] ?? ''),
    updatedAt: String(item['updatedAt'] ?? ''),
    isPrivate: Boolean(item['isPrivate']),
    notifyWhenReady: Boolean(
      item['isNotificationEnabled'] ?? item['notifyWhenReady']
    ),
    form: { schema: '', inputData: '' },
  };
}

export function mapServiceDraftDetail(raw: unknown): ServiceDraft {
  const item = asRecord(raw);
  return {
    ...mapServiceDraftListItem(raw),
    lastEditedAt: String(
      item['updatedAt'] ?? item['lastEditedAt'] ?? item['createdAt'] ?? ''
    ),
    form: readFormPayload(raw),
  };
}

export function toServiceDraftApiBody(
  body: ServiceDraftSaveRequest
): Record<string, unknown> {
  return {
    serviceId: /^\d+$/.test(body.serviceId)
      ? Number(body.serviceId)
      : body.serviceId,
    isPrivate: body.isPrivate,
    isNotificationEnabled: body.notifyWhenReady,
    form: body.form,
  };
}

/** Mirrors isActionItemAlreadyCompletedError: detects a stale/already-submitted 409 conflict. */
export function isServiceRequestConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const httpError = error as {
    status?: number;
    error?: ServiceApiError | string;
  };
  return httpError.status === 409;
}
